import { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Save, AlertCircle } from "lucide-react";

const LABELS = {
  no_login_days: "Aucune connexion depuis (j)",
  invoicing_no_invoice_days: "Aucune facture émise (j)",
  treasury_no_reco_days: "Aucune rapprochement bancaire (j)",
  hr_no_payslip_days: "Aucun bulletin de paie (j)",
  legal_no_activity_days: "Aucune activité juridique (j)",
  ai_no_use_days: "Aucun usage IA (j)",
  fleet_no_activity_days: "Aucune activité Fleet (j)",
  expenses_no_activity_days: "Aucune activité Dépenses (j)",
};

export default function StaffInactivity() {
  const [thresholds, setThresholds] = useState(null);
  const [defaults, setDefaults] = useState(null);
  const [flagged, setFlagged] = useState([]);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const [t, f] = await Promise.all([
        api.get("/staff/inactivity/thresholds"),
        api.get("/staff/inactivity/flagged"),
      ]);
      setThresholds(t.data.thresholds); setDefaults(t.data.defaults);
      setFlagged(f.data.flagged_clients || []);
    } catch (e) { toast.error(apiError(e)); }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/staff/inactivity/thresholds", { thresholds });
      toast.success("Seuils sauvegardés"); load();
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };

  if (!thresholds) return <div className="text-slate-400 p-6">Chargement…</div>;
  return (
    <div className="space-y-4" data-testid="staff-inactivity">
      <Card className="bg-slate-900 border-slate-800">
        <CardHeader className="flex-row items-center justify-between">
          <CardTitle className="text-sm text-slate-200">Seuils d'inactivité (jours)</CardTitle>
          <Button size="sm" onClick={save} disabled={saving} data-testid="save-thresholds-btn"><Save className="w-3.5 h-3.5 mr-1" />Enregistrer</Button>
        </CardHeader>
        <CardContent className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {Object.keys(defaults || {}).map(k => (
            <div key={k}>
              <label className="text-xs text-slate-400 block">{LABELS[k] || k}</label>
              <Input type="number" min="0" max="3650" value={thresholds[k] ?? defaults[k]}
                onChange={e => setThresholds({ ...thresholds, [k]: parseInt(e.target.value || "0", 10) })}
                className="bg-slate-950 border-slate-700 text-slate-200"
                data-testid={`threshold-${k}`} />
              <p className="text-[10px] text-slate-500 mt-0.5">Défaut : {defaults[k]}</p>
            </div>
          ))}
        </CardContent>
      </Card>
      <Card className="bg-slate-900 border-slate-800">
        <CardHeader><CardTitle className="text-sm text-slate-200">
          <AlertCircle className="w-4 h-4 inline mr-1 text-amber-400" />
          {flagged.length} client(s) inactif(s) au-delà des seuils
        </CardTitle></CardHeader>
        <CardContent className="space-y-2">
          {flagged.length === 0 ? <p className="text-sm text-slate-500 text-center py-4">Aucun client en inactivité</p> :
            flagged.map(c => (
              <div key={c.tenant_id} className="border border-slate-800 rounded p-3" data-testid={`flagged-${c.tenant_id}`}>
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-medium text-slate-200">{c.tenant_name}</span>
                  <Badge variant="outline" className="uppercase text-[10px]">{c.plan}</Badge>
                  <Badge className="bg-red-500/20 text-red-300 border-red-500/30 text-[10px]">{c.flag_count} flag(s)</Badge>
                </div>
                <div className="mt-1 flex flex-wrap gap-1">
                  {c.flags.map((f, i) => (
                    <Badge key={i} variant="outline" className="text-[10px] text-slate-400">
                      {LABELS[f.code] || f.code} : {f.days === "jamais" ? "jamais" : `${f.days}j`} (≥ {f.threshold})
                    </Badge>
                  ))}
                </div>
              </div>
            ))}
        </CardContent>
      </Card>
    </div>
  );
}
